import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'
import Divider from '@mui/material/Divider'
import LinearProgress from '@mui/material/LinearProgress'
import Chip from '@mui/material/Chip'

import { GlassCard } from '@components/common/Glass'
import ItemGuessBox, { Item } from '@components/common/ItemGuessBox'
import { OnlineRematchState, OnlineVoteState, Player } from 'src/pages/Dashboard'
import {
  altColor,
  altColorDarkText,
  altColorLighter,
  headingFont,
  mainColor,
  mainColorLighter,
  mainColorMediumText,
  shadow2,
} from 'src/ui/theme/tokens'
import { getTeamColor, getTeamColorDark, getTeamColorLighter } from 'src/utils/teamColors'
import { GameSounds } from 'src/ui/sound/types'
import EndGameView from './EndGameView'
import { List } from 'src/api/types/lists'
import { DailyListResult } from 'src/pages/Dashboard'
import { UserState } from 'src/app/AppContext'
import { AlertInput } from '@components/common/Alert'
import useWaterfallIntro from 'src/hooks/useWaterfallIntro'
import Tooltip from '@mui/material/Tooltip'
import FilterListIcon from '@mui/icons-material/FilterList'
import MenuIcon from '@mui/icons-material/Menu'
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded'
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded'

export type MultiPlayViewProps = {
  gameState: string
  mode: 'single' | 'multi' | 'host' | undefined
  multiMode: string
  fullScreen: boolean
  selectedList: number | undefined
  activePollId?: number
  contentType?: 'trivia' | 'poll'
  topic: string
  loading: boolean
  hostPlayMode: string
  orderedListItems: Item[]
  listItems: Item[]
  setListItems: (items: Item[]) => void
  myPlayer?: Player
  players: Player[]
  authoritativeOnlineAction?: {
    nonce: number
    selectedIndex: number
    result?: 'hit' | 'miss'
    actingTeam?: number
    teamTurn?: number
    teamOneScore?: number
    teamTwoScore?: number
  }
  onlineVoteState?: OnlineVoteState
  onlineMatchRated?: boolean
  onlineMatchTrophyDelta?: number
  multiplayerXpAwarded?: number
  onlineRematchState?: OnlineRematchState
  isOnlineHost?: boolean
  hostId?: string
  connectionId?: string
  ws?: WebSocket
  gameCode?: string
  sounds: GameSounds
  onShuffleComplete: (items: Item[]) => void
  onGameOver: () => void
  handleNewGame: (options?: { silentPollStart?: boolean; forceRandomOnlineLobby?: boolean }) => void
  handleHostPlayMode: (mode: string) => void
  handleEndGame: () => void
  handleRestartGame: () => void
  handlePlayAgainMatchmaking?: () => void
  handleLeaveOnlineLobby?: () => void
  handleLeaveMatchmakingSession?: () => void
  handleCreateList: () => void
  handleRateList: (rating: number) => void
  setRateAlert: (value: AlertInput) => void
  historyPush: (path: string) => void
  listRated?: number
  appUserId?: number
  user?: UserState
  doCreate: () => Promise<number | undefined>
  dailyList?: List
  dailyResult?: DailyListResult | undefined
}

const MultiPlayView = (props: MultiPlayViewProps) => {
  const {
    gameState,
    mode,
    multiMode,
    fullScreen,
    topic,
    loading,
    activePollId,
    contentType = activePollId ? 'poll' : 'trivia',
    handleNewGame,
    handleHostPlayMode,
    hostPlayMode,
    orderedListItems,
    listItems,
    setListItems,
    myPlayer,
    players,
    authoritativeOnlineAction,
    onlineVoteState,
    onlineMatchRated,
    onlineMatchTrophyDelta,
    multiplayerXpAwarded,
    onlineRematchState,
    isOnlineHost,
    hostId,
    connectionId,
    ws,
    gameCode,
    sounds,
    onShuffleComplete,
    onGameOver,
    handleEndGame,
    handleRestartGame,
    handlePlayAgainMatchmaking,
    handleLeaveOnlineLobby,
    handleLeaveMatchmakingSession,
    handleCreateList,
    handleRateList,
    setRateAlert,
    historyPush,
    listRated,
    appUserId,
    user,
    doCreate,
    dailyList,
    dailyResult,
    selectedList,
  } = props

  const [displayItems, setDisplayItems] = useState<Item[]>([])
  const [displayTitle, setDisplayTitle] = useState<string>('')
  const itemsReady = displayItems.length > 0
  const showGameLoading = loading || (!listItems.length && !displayItems.length)
  const [teamTurn, setTeamTurn] = useState(1)
  const [teamOneScore, setTeamOneScore] = useState(0)
  const [teamTwoScore, setTeamTwoScore] = useState(0)
  const [teamWinner, setTeamWinner] = useState<number | undefined>(0)
  const [movedIds, setMovedIds] = useState<number[]>([])
  const hasAnyGuess =
    listItems.length > 0 && listItems.some((item) => item.team || item.guessed || item.pendingTeam)
  const [introEnabled, setIntroEnabled] = useState(false)
  const [voteNowMs, setVoteNowMs] = useState<number>(Date.now())
  const [onlineMissFeedback, setOnlineMissFeedback] = useState<
    | {
        nonce: number
        selectedIndex: number
        label: 'Warm' | 'Cold'
        color: string
      }
    | undefined
  >(undefined)
  const resolvedMyTeam =
    myPlayer?.team ?? players.find((player) => player.id === connectionId)?.team
  const teamOnePlayer = players.find((player) => player.team === 1)
  const teamTwoPlayer = players.find((player) => player.team === 2)
  const isHeadToHeadOnline = multiMode === 'online' && players.length === 2
  const rankedHeadToHeadNames =
    isHeadToHeadOnline &&
    onlineMatchRated &&
    !!teamOnePlayer?.name &&
    !!teamTwoPlayer?.name &&
    teamOnePlayer.name.trim().length > 0 &&
    teamTwoPlayer.name.trim().length > 0
  const primaryLabel = isHeadToHeadOnline
    ? rankedHeadToHeadNames
      ? resolvedMyTeam === 1
        ? teamOnePlayer?.name || 'You'
        : teamTwoPlayer?.name || 'You'
      : 'You'
    : 'Blue'
  const secondaryLabel = isHeadToHeadOnline
    ? rankedHeadToHeadNames
      ? resolvedMyTeam === 1
        ? teamTwoPlayer?.name || 'Opponent'
        : teamOnePlayer?.name || 'Opponent'
      : 'Opponent'
    : 'Orange'
  const primaryTeam = isHeadToHeadOnline ? (resolvedMyTeam === 1 ? 1 : 2) : 2
  const secondaryTeam = isHeadToHeadOnline ? (primaryTeam === 1 ? 2 : 1) : 1
  const primaryScore = isHeadToHeadOnline
    ? primaryTeam === 1
      ? teamOneScore
      : teamTwoScore
    : teamTwoScore
  const secondaryScore = isHeadToHeadOnline
    ? secondaryTeam === 1
      ? teamOneScore
      : teamTwoScore
    : teamOneScore
  const didIntroRef = useRef(false)
  const onlineMissFeedbackTimerRef = useRef<number | undefined>(undefined)
  const listContainerRef = useRef<HTMLDivElement | null>(null)
  const listItemRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const prevItemPositions = useRef<Map<number, DOMRect>>(new Map())
  const prevOrderRef = useRef<number[]>([])
  const didMeasureList = useRef(false)
  const skipNextFlipRef = useRef(false)
  const lastDisplayKeyRef = useRef<string>('')
  const skeletonCount = 10
  const sourceItems = multiMode === 'online' ? listItems : orderedListItems
  const endgameSoundKeyRef = useRef<string>('')
  const onlineDisplayOrder = React.useCallback(
    (items: Item[]): Item[] => {
      if (multiMode !== 'online') return items
      const guessed = items
        .filter((item) => item.team !== undefined)
        .sort((a, b) => a.index - b.index)
      const remaining = items.filter((item) => item.team === undefined)
      return guessed.concat(remaining)
    },
    [multiMode]
  )

  const introKey = React.useMemo(() => {
    const ids = sourceItems.map((item) => String(item.title ?? item.index)).sort()
    const seed = selectedList ?? activePollId ?? topic ?? ''
    return `${seed}:${ids.join('|')}`
  }, [sourceItems, selectedList, activePollId, topic])

  const { visibleCount, isAnimating: isIntroAnimating } = useWaterfallIntro({
    key: introKey,
    count: displayItems.length,
    enabled: introEnabled && displayItems.length > 0,
    onStep: () => sounds.swipe(),
    onComplete: () => {
      setIntroEnabled(false)
      if (multiMode !== 'online' && displayItems.length) {
        onShuffleComplete(displayItems)
      }
    },
  })

  const clearOnlineMissFeedbackTimer = () => {
    if (onlineMissFeedbackTimerRef.current) {
      window.clearTimeout(onlineMissFeedbackTimerRef.current)
      onlineMissFeedbackTimerRef.current = undefined
    }
  }

  useEffect(() => {
    if (gameState !== 'pre') return

    setTeamTurn(1)
    setTeamOneScore(0)
    setTeamTwoScore(0)
    setTeamWinner(0)
    setMovedIds([])
    if (multiMode !== 'online') {
      setDisplayItems([])
      setDisplayTitle('')
      lastDisplayKeyRef.current = ''
    }
  }, [gameState, selectedList, activePollId])

  useEffect(() => {
    if (multiMode === 'online') return
    if (!hasAnyGuess) {
      setTeamTurn(1)
      setTeamOneScore(0)
      setTeamTwoScore(0)
      setTeamWinner(0)
    }
  }, [multiMode, hasAnyGuess])

  useEffect(() => {
    if (multiMode === 'online') return
    if (!sourceItems.length) return
    if (hasAnyGuess) return
    const nextKey = sourceItems
      .map((item) => `${item.title}-${item.index}-${item.team ?? ''}`)
      .join('|')
    if (nextKey === lastDisplayKeyRef.current) return
    lastDisplayKeyRef.current = nextKey
    setDisplayItems(sourceItems)
  }, [multiMode, sourceItems, hasAnyGuess])

  useEffect(() => {
    if (teamOneScore - teamTwoScore < 0) {
      setTeamWinner(2)
    } else if (teamOneScore - teamTwoScore > 0) {
      setTeamWinner(1)
    } else {
      setTeamWinner(0)
    }
  }, [teamOneScore, teamTwoScore])

  useEffect(() => {
    if (!loading && sourceItems.length) {
      if (!displayItems.length || gameState === 'pre') {
        setDisplayItems(onlineDisplayOrder(sourceItems))
      }
    }
    if (!loading && topic) {
      setDisplayTitle(topic)
    }
  }, [loading, sourceItems, topic, displayItems.length, gameState, onlineDisplayOrder])

  useEffect(() => {
    if (gameState === 'pre' || !sourceItems.length) {
      didIntroRef.current = false
      setIntroEnabled(false)
      return
    }
    setDisplayItems(onlineDisplayOrder(sourceItems))
    if (!didIntroRef.current) {
      didIntroRef.current = true
      setIntroEnabled(true)
    }
  }, [gameState, sourceItems, onlineDisplayOrder])

  useEffect(() => {
    if (!authoritativeOnlineAction) return
    if (authoritativeOnlineAction.selectedIndex >= 0) {
      if (authoritativeOnlineAction.result === 'miss') {
        applyAuthoritativeMiss(
          authoritativeOnlineAction.selectedIndex,
          authoritativeOnlineAction.actingTeam
        )
      } else {
        applyAuthoritativeHit(
          authoritativeOnlineAction.selectedIndex,
          authoritativeOnlineAction.actingTeam || 1
        )
      }
    }
    if (typeof authoritativeOnlineAction.teamTurn === 'number') {
      setTeamTurn(authoritativeOnlineAction.teamTurn)
    }
    if (typeof authoritativeOnlineAction.teamOneScore === 'number') {
      setTeamOneScore(authoritativeOnlineAction.teamOneScore)
    }
    if (typeof authoritativeOnlineAction.teamTwoScore === 'number') {
      setTeamTwoScore(authoritativeOnlineAction.teamTwoScore)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authoritativeOnlineAction?.nonce])

  useEffect(() => {
    return () => {
      didIntroRef.current = false
    }
  }, [])

  useEffect(() => {
    return () => clearOnlineMissFeedbackTimer()
  }, [])

  useEffect(() => {
    if (!onlineVoteState) return
    setVoteNowMs(Date.now())
    const timer = window.setInterval(() => setVoteNowMs(Date.now()), 250)
    return () => window.clearInterval(timer)
  }, [onlineVoteState?.deadlineAtMs, onlineVoteState?.team])

  const isVotePendingForTurn =
    multiMode === 'online' && !!onlineVoteState && onlineVoteState.team === teamTurn
  const pendingVoteTeamSize =
    onlineVoteState?.team !== undefined
      ? players.filter((player) => player.team === onlineVoteState.team).length
      : 0
  const showVoteCountdown = isVotePendingForTurn && pendingVoteTeamSize > 1
  const voteSummary = React.useMemo(() => {
    if (!onlineVoteState) return []
    const counts = new Map<number, number>()
    onlineVoteState.votes.forEach((vote) => {
      counts.set(vote.selectedIndex, (counts.get(vote.selectedIndex) || 0) + 1)
    })
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
  }, [onlineVoteState])
  const pendingSelectedIndex = voteSummary.length ? voteSummary[0]?.[0] : undefined
  const voteProgressValue = onlineVoteState
    ? Math.max(0, Math.min(100, ((onlineVoteState.deadlineAtMs - voteNowMs) / 10000) * 100))
    : 0
  const voteParticipantsByIndex = React.useMemo(() => {
    const map = new Map<number, Array<{ id: string; icon: string; name: string }>>()
    if (!onlineVoteState) return map
    onlineVoteState.votes.forEach((vote) => {
      const teammate = players.find((player) => player.id === vote.connectionId)
      if (!teammate) return
      const key = vote.selectedIndex
      const row = map.get(key) || []
      row.push({
        id: vote.connectionId,
        icon: teammate.icon || '🙂',
        name: teammate.name,
      })
      map.set(key, row)
    })
    return map
  }, [onlineVoteState, players])

  const handleMiss = () => {
    sounds.select()
    setTeamTurn((prev) => (prev % 2) + 1)
  }

  function applyAuthoritativeMiss(selectedIndex: number, actingTeam?: number): void {
    const nextList = listItems.slice()
    const targetIndex = nextList.findIndex((item) => item.index === selectedIndex)
    if (targetIndex < 0) return
    const target = nextList[targetIndex]
    if (!target) return
    if (target.team !== undefined || target.guessed || target.pendingTeam) return
    const remaining = nextList
      .filter((entry) => entry.team === undefined)
      .map((entry) => entry.index)
      .sort((a, b) => a - b)
    const position = remaining.indexOf(selectedIndex)
    const hasEnoughItems = remaining.length > 5
    let missLabel: 'Warm' | 'Cold' | undefined = undefined
    if (hasEnoughItems) {
      if (position >= 0 && position <= 2) {
        missLabel = 'Warm'
      } else if (position >= remaining.length - 3) {
        missLabel = 'Cold'
      }
    }
    target.guessed = true
    nextList[targetIndex] = target
    setListItems(nextList)
    setDisplayItems(onlineDisplayOrder(nextList))
    sounds.click()
    if (missLabel) {
      setOnlineMissFeedback({
        nonce: Date.now(),
        selectedIndex,
        label: missLabel,
        color: missLabel === 'Warm' ? '#f59e0b' : '#38bdf8',
      })
      clearOnlineMissFeedbackTimer()
      onlineMissFeedbackTimerRef.current = window.setTimeout(() => {
        setOnlineMissFeedback(undefined)
      }, 950)
    } else {
      clearOnlineMissFeedbackTimer()
      setOnlineMissFeedback(undefined)
    }
  }

  function applyAuthoritativeHit(selectedIndex: number, actingTeam: number): void {
    const nextList = listItems.slice()
    const targetIndex = nextList.findIndex((item) => item.index === selectedIndex)
    if (targetIndex < 0) return
    const target = nextList[targetIndex]
    if (!target) return
    if (target.team !== undefined) return
    target.team = actingTeam
    target.guessed = false
    delete target.pendingTeam
    nextList[targetIndex] = target

    const resetGuessedList = nextList.map((item) => ({
      ...item,
      guessed: false,
    }))
    setListItems(resetGuessedList)
    setDisplayItems(onlineDisplayOrder(resetGuessedList))

    setMovedIds((prev) => (prev.includes(selectedIndex) ? prev : prev.concat(selectedIndex)))
    setTimeout(() => {
      setMovedIds((prev) => prev.filter((id) => id !== selectedIndex))
    }, 320)

    if (multiMode === 'online' && mode === 'multi') {
      if (resolvedMyTeam && actingTeam === resolvedMyTeam) {
        sounds.celebrate()
      } else {
        sounds.wrong()
      }
    }

    const allSelected = resetGuessedList.every(
      (item) => item.team !== undefined || item.pendingTeam
    )
    if (allSelected) {
      onGameOver()
    }
  }

  const handleItemClick = async (item: Item) => {
    const index = item.index
    const newList = listItems.slice()
    const newItemIndex = newList.findIndex((l) => l.index === index)
    if (newItemIndex < 0) return
    const newItem = newList[newItemIndex]
    if (!newItem) return
    if (newItem?.team || newItem.guessed) return
    if (newItem.team !== undefined) return

    const availableIndices = listItems
      .map((entry) => (entry.team === undefined ? entry.index : undefined))
      .filter((value): value is number => typeof value === 'number')
    const minAvailableIndex = Math.min(...availableIndices)

    if (
      minAvailableIndex < newItem.index &&
      (mode === 'host' ? hostPlayMode !== 'any-order' : true)
    ) {
      sounds.click()
      newItem.guessed = true
      newList[newItemIndex] = newItem
      setListItems(newList)
      setTeamTurn((prev) => (prev % 2) + 1)
      return
    }

    const score = 1
    if (mode === 'multi') {
      if (multiMode !== 'local' && myPlayer?.team === teamTurn) {
        sounds.celebrate()
      }
      if (teamTurn === 1) {
        sounds.celebrate()
        setTeamOneScore((prev) => prev + score)
      } else if (teamTurn === 2) {
        sounds.celebrate()
        setTeamTwoScore((prev) => prev + score)
      }
    } else if (mode === 'host') {
      sounds.celebrate()
      if (teamTurn === 1) {
        setTeamOneScore((prev) => prev + score)
      } else if (teamTurn === 2) {
        setTeamTwoScore((prev) => prev + score)
      }
    }

    newItem.team = teamTurn
    delete newItem.pendingTeam
    newList[newItemIndex] = newItem

    const resetGuessedList = newList.map((l) => ({ ...l, guessed: false }))
    const guessed = resetGuessedList.filter((l) => l.team).sort((a, b) => a.index - b.index)
    const remaining = resetGuessedList.filter((l) => !l.team)
    const nextList = guessed.concat(remaining)
    setListItems(nextList)
    setDisplayItems(nextList)

    setMovedIds((prev) => (prev.includes(index) ? prev : prev.concat(index)))
    setTimeout(() => {
      setMovedIds((prev) => prev.filter((id) => id !== index))
    }, 320)

    const allSelected = nextList.every((entry) => entry.team !== undefined || entry.pendingTeam)
    if (allSelected) {
      onGameOver()
    }
  }

  useEffect(() => {
    if (gameState !== 'over') return
    if (mode !== 'multi' || multiMode !== 'online') return
    const key = `${teamWinner || 0}-${teamOneScore}-${teamTwoScore}-${resolvedMyTeam || 0}`
    if (endgameSoundKeyRef.current === key) return
    endgameSoundKeyRef.current = key
    if (teamWinner && resolvedMyTeam && teamWinner === resolvedMyTeam) {
      sounds.win()
    } else {
      sounds.loss()
    }
  }, [gameState, mode, multiMode, teamWinner, teamOneScore, teamTwoScore, resolvedMyTeam, sounds])

  useEffect(() => {
    if (gameState !== 'over') {
      endgameSoundKeyRef.current = ''
    }
  }, [gameState])

  useLayoutEffect(() => {
    if (mode === 'single' || gameState === 'pre') return
    if (isIntroAnimating) return
    const container = listContainerRef.current
    if (!container) return

    const nextOrder = sourceItems.map((item) => item.index)
    const orderUnchanged =
      prevOrderRef.current.length === nextOrder.length &&
      prevOrderRef.current.every((value, i) => value === nextOrder[i])

    const newPositions = new Map<number, DOMRect>()
    listItemRefs.current.forEach((el, id) => {
      if (el) {
        const rect = el.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        const relative = new DOMRect(
          rect.left - containerRect.left,
          rect.top - containerRect.top,
          rect.width,
          rect.height
        )
        newPositions.set(id, relative)
      }
    })

    if (!didMeasureList.current) {
      prevItemPositions.current = newPositions
      didMeasureList.current = true
      prevOrderRef.current = nextOrder
      return
    }

    if (orderUnchanged) {
      prevItemPositions.current = newPositions
      prevOrderRef.current = nextOrder
      return
    }

    if (skipNextFlipRef.current) {
      skipNextFlipRef.current = false
      prevItemPositions.current = newPositions
      prevOrderRef.current = nextOrder
      return
    }

    prevItemPositions.current.forEach((prevRect, id) => {
      const newRect = newPositions.get(id)
      const el = listItemRefs.current.get(id)
      if (!newRect || !el) return
      const dx = prevRect.left - newRect.left
      const dy = prevRect.top - newRect.top
      if (dx || dy) {
        el.animate(
          [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
          {
            duration: 280,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          }
        )
      }
    })

    prevItemPositions.current = newPositions
    prevOrderRef.current = nextOrder
  }, [sourceItems, mode, gameState, isIntroAnimating])

  const isMatchmakingSession = multiMode === 'online' && !!handlePlayAgainMatchmaking
  const canControlOnlineActions =
    isMatchmakingSession ||
    multiMode !== 'online' ||
    !!isOnlineHost ||
    (!!hostId && !!connectionId && hostId === connectionId) ||
    (!!connectionId && players[0]?.id === connectionId)

  return gameState !== 'pre' && mode !== 'single' ? (
    <>
      <EndGameView
        gameState={gameState}
        multiMode={multiMode}
        fullScreen={fullScreen}
        mode={mode}
        teamOneScore={teamOneScore}
        teamTwoScore={teamTwoScore}
        teamWinner={teamWinner}
        selectedList={selectedList}
        activePollId={activePollId}
        hostPlayMode={hostPlayMode}
        listItems={listItems}
        handleMiss={handleMiss}
        handleNewGame={handleNewGame}
        handleHostPlayMode={handleHostPlayMode}
        soloResult="active"
        soloGuessCount={0}
        dailyList={dailyList}
        dailyResult={dailyResult}
        loading={loading}
        listRated={listRated}
        appUserId={appUserId}
        user={user}
        doCreate={doCreate}
        topic={topic}
        sounds={sounds}
        handleEndGame={handleEndGame}
        handleRestartGame={handleRestartGame}
        handlePlayAgainMatchmaking={handlePlayAgainMatchmaking}
        onLeaveOnlineLobby={handleLeaveOnlineLobby}
        onLeaveMatchmaking={handleLeaveMatchmakingSession}
        onlineRematchState={onlineRematchState}
        onlineMatchRated={onlineMatchRated}
        onlineMatchTrophyDelta={onlineMatchTrophyDelta}
        multiplayerXpAwarded={multiplayerXpAwarded}
        players={players}
        connectionId={connectionId}
        handleCreateList={handleCreateList}
        handleRateList={handleRateList}
        setRateAlert={setRateAlert}
        historyPush={historyPush}
        canControlOnlineActions={canControlOnlineActions}
      />

      <Stack spacing={1.25}>
        <GlassCard
          sx={{
            px: {
              xs: 1.5,
              sm: 2,
              md: 2,
            },
            py: {
              xs: 1.5,
              sm: 2,
              md: 2,
              lg: 2,
            },
            mt: 0,
            borderRadius: '20px',
            border: `1.5px solid ${getTeamColor(teamTurn)}`,
            background: `linear-gradient(135deg, ${getTeamColorLighter(
              teamTurn
            )} 0%, rgba(255,255,255,0.9) 100%)`,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0} width="100%">
              <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                <Typography
                  fontSize={fullScreen ? 15 : 17}
                  fontWeight={600}
                  // color={getTeamColor(primaryTeam)}
                  sx={{
                    color: getTeamColorDark(primaryTeam),
                    px: fullScreen ? 1 : 2,
                    py: fullScreen ? 0 : 0.33,
                    borderRadius: '12px',
                    borderStyle: 'solid',
                    borderWidth: fullScreen ? '1px' : '1.5px',
                    borderColor: getTeamColor(primaryTeam),
                    backgroundColor: getTeamColorLighter(primaryTeam),
                  }}
                >
                  {primaryLabel}:{' '}
                  <Box
                    component="span"
                    data-rt-display
                    sx={{ fontFamily: headingFont, fontWeight: 800 }}
                  >
                    {primaryScore}
                  </Box>
                </Typography>
                <Typography
                  fontSize={fullScreen ? 15 : 17}
                  fontWeight={600}
                  // color={getTeamColor(secondaryTeam)}
                  sx={{
                    color: getTeamColorDark(secondaryTeam),
                    px: fullScreen ? 1 : 2,
                    py: fullScreen ? 0 : 0.33,
                    borderRadius: '12px',
                    borderStyle: 'solid',
                    borderWidth: fullScreen ? '1px' : '1.5px',
                    borderColor: getTeamColor(secondaryTeam),
                    backgroundColor: getTeamColorLighter(secondaryTeam),
                  }}
                >
                  {secondaryLabel}:{' '}
                  <Box
                    component="span"
                    data-rt-display
                    sx={{ fontFamily: headingFont, fontWeight: 800 }}
                  >
                    {secondaryScore}
                  </Box>
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="flex-start"
                width="100%"
                mb={1}
                gap={0}
                sx={{ position: 'relative' }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    minHeight: 32,
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Skeleton
                    variant="text"
                    width={fullScreen ? '70%' : '45%'}
                    height={32}
                    sx={{
                      opacity: showGameLoading ? 0.6 : 0,
                      transition: 'opacity 250ms ease',
                      borderRadius: '8px',
                      position: 'absolute',
                    }}
                    animation="wave"
                  />
                  <Typography
                    fontWeight={800}
                    fontSize={20}
                    variant="h6"
                    sx={{
                      opacity: showGameLoading ? 0 : 1,
                      transition: 'opacity 250ms ease',
                    }}
                  >
                    {displayTitle || 'RankTrivia'}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                <Chip
                  variant="outlined"
                  size="small"
                  clickable={false}
                  label={
                    <Box display="flex" gap={0.5} alignItems="center">
                      <HowToVoteRoundedIcon fontSize="inherit" sx={{ fontSize: '13px' }} />
                      <Typography variant="caption" fontWeight={700}>
                        {contentType === 'poll' ? 'Poll' : 'Trivia'}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: contentType === 'poll' ? altColorLighter : mainColorLighter,
                    borderColor: contentType === 'poll' ? altColor : mainColor,
                    color: contentType === 'poll' ? altColorDarkText : mainColorMediumText,
                  }}
                />
                <Typography
                  fontSize={16}
                  fontWeight={600}
                  sx={{
                    color: getTeamColorDark(teamTurn),
                  }}
                >
                  {teamTurn === 1 ? 'Orange' : 'Blue'}&apos;s turn
                </Typography>
              </Box>
            </Box>
          </Box>

          {gameState === 'active' && mode === 'host' ? (
            <Stack spacing={1} mt={1}>
              <Divider />

              <Box display="flex" justifyContent="space-between">
                <Box
                  onClick={() =>
                    handleHostPlayMode(hostPlayMode === 'in-order' ? 'any-order' : 'in-order')
                  }
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    cursor: 'pointer',
                    borderRadius: '12px',
                    textAlign: 'center',
                    '@media (hover: hover) and (pointer: fine)': {
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: shadow2,
                      },
                    },
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: '#ccc',
                    background:
                      'linear-gradient(135deg, rgba(240,240,240,0.85) 0%, rgba(250,250,250,0.6) 100%)',
                    '&:active': {
                      transform: 'scale(0.99)',
                    },
                  }}
                >
                  <Tooltip
                    arrow
                    title={
                      <Typography variant="subtitle2">
                        {hostPlayMode === 'in-order'
                          ? 'Items must be guessed in-order.'
                          : 'Items can be guessed in any-order.'}{' '}
                        Click to toggle!
                      </Typography>
                    }
                  >
                    <Box display="flex" justifyContent="center" gap={0.5} alignItems="center">
                      {hostPlayMode === 'in-order' ? (
                        <FilterListIcon fontSize="small" color="action" />
                      ) : (
                        <MenuIcon fontSize="small" color="action" />
                      )}
                      <Typography variant="body1" fontWeight={500}>
                        {hostPlayMode === 'in-order' ? 'In-Order' : 'Any-Order'}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>

                <Box
                  onClick={() => handleMiss()}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    cursor: 'pointer',
                    borderRadius: '12px',
                    textAlign: 'center',
                    '@media (hover: hover) and (pointer: fine)': {
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: shadow2,
                      },
                    },
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: '#ccc',
                    background:
                      'linear-gradient(135deg, rgba(240,240,240,0.85) 0%, rgba(250,250,250,0.6) 100%)',
                    '&:active': {
                      transform: 'scale(0.99)',
                    },
                  }}
                >
                  <Tooltip
                    arrow
                    title={
                      <Typography variant="subtitle2">
                        (Click to Change Team Turn / Guess not in list)
                      </Typography>
                    }
                  >
                    <Box display="flex" justifyContent="center" gap={0.5} alignItems="center">
                      <HighlightOffRoundedIcon fontSize="small" color="action" />
                      <Typography variant="body1" fontWeight={500}>
                        Miss!
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            </Stack>
          ) : null}
        </GlassCard>

        <Box
          ref={listContainerRef}
          sx={{
            position: 'relative',
            minHeight: !itemsReady ? skeletonCount * 54 : undefined,
          }}
        >
          {showVoteCountdown ? (
            <LinearProgress
              variant="determinate"
              value={voteProgressValue}
              sx={{
                mb: 0.75,
                height: 4,
                borderRadius: '999px',
                backgroundColor: 'rgba(148,163,184,0.2)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: '999px',
                },
              }}
            />
          ) : null}
          <Stack
            spacing={0.66}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              opacity: itemsReady ? 0 : 1,
              pointerEvents: 'none',
              transition: 'opacity 300ms ease',
            }}
          >
            {Array.from({ length: skeletonCount }).map((_, i) => (
              <GlassCard
                key={`multi-skel-${i}`}
                sx={{
                  px: 1.5,
                  py: 1.5,
                  borderRadius: '28px',
                  background: '#ffffff',
                }}
              >
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Skeleton
                    variant="text"
                    width={`${50 - (i % 5) * 6}%`}
                    height={fullScreen ? 27 : 30}
                    animation="wave"
                  />
                </Box>
              </GlassCard>
            ))}
          </Stack>
          <Stack
            spacing={0.66}
            sx={{
              opacity: itemsReady ? 1 : 0,
              transition: 'opacity 300ms ease',
              pointerEvents: itemsReady && !isIntroAnimating ? 'auto' : 'none',
            }}
          >
            {(displayItems.length ? displayItems : onlineDisplayOrder(sourceItems)).map(
              (item: Item, idx: number) => {
                const isVisible = idx < visibleCount
                const introSx = isIntroAnimating
                  ? {
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'translateY(0px)' : 'translateY(18px)',
                    }
                  : { opacity: 1, transform: 'translateY(0px)' }
                return (
                  <Box
                    key={`item-${item.index}`}
                    ref={(el) => {
                      if (el) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        listItemRefs.current.set(item.index, el as any)
                      } else {
                        listItemRefs.current.delete(item.index)
                      }
                    }}
                    sx={{
                      transition: 'opacity 420ms ease, transform 420ms ease',
                      ...introSx,
                    }}
                  >
                    <ItemGuessBox
                      item={item}
                      list={listItems}
                      mode={mode}
                      hostMode={hostPlayMode}
                      teamTurn={teamTurn}
                      fullScreen={fullScreen}
                      showGameLoading={showGameLoading}
                      showSearch={gameState === 'over'}
                      moved={movedIds.includes(item.index)}
                      voteParticipants={
                        showVoteCountdown ? voteParticipantsByIndex.get(item.index) || [] : []
                      }
                      isVotePending={
                        showVoteCountdown &&
                        typeof pendingSelectedIndex === 'number' &&
                        pendingSelectedIndex === item.index
                      }
                      enableFeedback={!(mode === 'multi' && multiMode === 'online')}
                      enableHitFeedback={!(mode === 'multi' && multiMode === 'online')}
                      externalFeedback={
                        onlineMissFeedback &&
                        onlineMissFeedback.selectedIndex === item.index &&
                        onlineMissFeedback.nonce
                          ? {
                              nonce: onlineMissFeedback.nonce,
                              label: onlineMissFeedback.label,
                              color: onlineMissFeedback.color,
                            }
                          : undefined
                      }
                      externalFeedbackIndex={onlineMissFeedback?.selectedIndex}
                      externalFeedbackNonce={onlineMissFeedback?.nonce}
                      disabled={
                        isIntroAnimating ||
                        (mode !== 'host' && multiMode !== 'local' && teamTurn !== myPlayer?.team)
                      }
                      onClick={async () => {
                        if (
                          mode !== 'host' &&
                          multiMode !== 'local' &&
                          teamTurn !== myPlayer?.team
                        ) {
                          return
                        }
                        if (
                          mode === 'multi' &&
                          (multiMode === 'party' || multiMode === 'online') &&
                          !item.team &&
                          gameCode
                        ) {
                          ws?.send(
                            JSON.stringify({
                              event: 'selectOption',
                              data: {
                                userId: myPlayer?.id || '',
                                code: gameCode,
                                selectedIndex: item.index,
                              },
                            })
                          )
                        } else {
                          await handleItemClick(item)
                        }
                      }}
                    />
                  </Box>
                )
              }
            )}
          </Stack>
        </Box>
      </Stack>
    </>
  ) : null
}

export default MultiPlayView
